import { useState, useEffect } from "react";
import { useAuth } from "@/core/auth/useAuth";

import { useCfaSwitcher } from "@/core/cfa/useCfaSwitcher";
import { Button } from "@/shared/ui/button";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useNavigationItems } from '@/shared/hooks/useNavigationItems';
import { logger } from '@/shared/utils/logger';
import DebugPerformancePanel from '@/core/performance/components/DebugPerformancePanel';
import { SyncStatusBadge } from '@/shared/components/SyncStatusBadge';
import { UserMenu } from '@/shared/components/UserMenu';
import { CfaSwitcher } from '@/shared/components/CfaSwitcher';
import { EducatoreAreaSwitcher } from '@/shared/components/EducatoreAreaSwitcher';
import { AdminShopAreaSwitcher } from '@/shared/components/AdminShopAreaSwitcher';
import { isAdminAreaOnlyUser } from '@/core/permissions/adminAreaAccess';


const Header = () => {
  const { user, profile, hasRole, roles, isLoading: authLoading } = useAuth();
  const { cfaName } = useCfaSwitcher();
  const { groups: navGroups } = useNavigationItems();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

  // Helper: utente con soli ruoli dell'area amministrativa.
  // Vede /amministrazione piu' i moduli concessi (es. shop), non il resto.
  const isOnlyAmministrazione = isAdminAreaOnlyUser(hasRole);

  // Log temporaneo per debug
  useEffect(() => {
    logger.debug('🔍 Auth state:', {
      user: !!user,
      authLoading,
      rolesCount: roles.length,
      roles: roles.map(r => r.role_code)
    });
  }, [user, authLoading, roles]);

  // Aggiorna il meta title in base allo stato di login
  useEffect(() => {
    document.title = user ? (cfaName || 'CFA') : '***';
  }, [user, cfaName]);
  
  // Gestione indicatori di scroll
  useEffect(() => {
    if (isSideMenuOpen) {
      const nav = document.querySelector('nav.overflow-y-auto');
      const topIndicator = document.getElementById('scroll-indicator-top');
      const bottomIndicator = document.getElementById('scroll-indicator-bottom');
      
      const handleScroll = () => {
        if (nav && topIndicator && bottomIndicator) {
          const { scrollTop, scrollHeight, clientHeight } = nav;
          
          // Mostra/nascondi indicatore superiore
          if (scrollTop > 10) {
            topIndicator.classList.add('opacity-100');
            topIndicator.classList.remove('opacity-0');
          } else {
            topIndicator.classList.add('opacity-0');
            topIndicator.classList.remove('opacity-100');
          }
          
          // Mostra/nascondi indicatore inferiore
          if (scrollTop + clientHeight < scrollHeight - 10) {
            bottomIndicator.classList.add('opacity-100');
            bottomIndicator.classList.remove('opacity-0');
          } else {
            bottomIndicator.classList.add('opacity-0');
            bottomIndicator.classList.remove('opacity-100');
          }
        }
      };
      
      if (nav) {
        nav.addEventListener('scroll', handleScroll);
        // Trigger iniziale per impostare lo stato corretto
        handleScroll();
        
        return () => nav.removeEventListener('scroll', handleScroll);
      }
    }
  }, [isSideMenuOpen]);

  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  const closeSideMenu = () => {
    setIsSideMenuOpen(false);
  };

  return (
    <>
      {/* Header minimalista Swiss Style: Logo + Burger + User Area */}
      <header className="bg-background border-b border-border relative z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Burger Menu Button - nascosto per utenti solo-amministrazione */}
            {user && !isOnlyAmministrazione && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSideMenu}
                className="p-2"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            
            <Link to="/" className="text-xl font-bold text-primary">
              {user ? <CfaSwitcher /> : '***'}
            </Link>
          </div>
          
          {/* User Area - Clean Swiss Style */}
          <div className="flex items-center gap-3">
            {/* Debug Performance Panel - only in development */}
            {import.meta.env.DEV && <DebugPerformancePanel />}

            {/* Area Switcher for educatore-only users */}
            {user && hasRole('educatore') && !hasRole('admin') && !hasRole('super-admin') && (
              <EducatoreAreaSwitcher />
            )}

            {/* Passaggio allo Shop per l'area amministrativa (fuori dal gate del menu) */}
            <AdminShopAreaSwitcher />

            {/* Sync Status Badge - nascosto per utenti solo-amministrazione */}
            {user && !isOnlyAmministrazione && <SyncStatusBadge compact />}

            {/* TODO: RIMUOVERE - Pulsante temporaneo per forzare sync - nascosto per solo-amministrazione */}
            {user && !isOnlyAmministrazione && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  import('@/core/offline/useOfflinePreloader').then(({ useOfflinePreloader }) => {
                    // Trigger preload manualmente - questo è un workaround
                    window.dispatchEvent(new CustomEvent('force-preload'));
                  });
                }}
                className="h-8 px-2 text-xs bg-yellow-100 hover:bg-yellow-200 border-yellow-300 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 dark:border-yellow-700"
                title="Forza pre-caricamento dati offline"
              >
                <i className="fas fa-sync mr-1" />
                Sync
              </Button>
            )}

            {user ? (
              <UserMenu
                userName={`${profile?.nome || ''}${profile?.cognome ? ` ${profile.cognome.charAt(0)}.` : ''}`}
              />
            ) : (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="min-h-[44px] px-4 font-medium"
              >
                <Link to="/auth">Accedi</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Overlay */}
      {isSideMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 z-40"
          onClick={closeSideMenu}
        />
      )}

      {/* Side Menu */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-white dark:bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${
          isSideMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Menu</h2>
          <Button variant="ghost" size="sm" onClick={closeSideMenu}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <nav className="relative p-4 pb-20 space-y-2 overflow-y-auto flex-1 overscroll-contain -webkit-overflow-scrolling-touch">
          {/* Indicatore di scroll superiore */}
          <div className="sticky top-0 left-0 right-0 h-8 -mt-4 mb-4 bg-gradient-to-b from-white dark:from-gray-900 to-transparent pointer-events-none opacity-0 transition-opacity duration-200 z-10" id="scroll-indicator-top" />
          
          {user && (
            <>
              {/* Mostra un indicatore se i ruoli sono in caricamento */}
              {authLoading && (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 italic">
                  Caricamento menu...
                </div>
              )}

              {navGroups.map((group, gIdx) => (
                <div key={group.label} className={gIdx > 0 ? "mt-4" : undefined}>
                  <p className="px-2 mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setIsSideMenuOpen(false)}
                          className="flex items-center gap-3 px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
          
          {/* Indicatore di scroll inferiore */}
          <div className="sticky bottom-0 left-0 right-0 h-8 -mb-20 bg-gradient-to-t from-white dark:from-gray-900 to-transparent pointer-events-none opacity-0 transition-opacity duration-200 z-10" id="scroll-indicator-bottom" />
        </nav>
      </div>
    </>
  );
};

export default Header;
